import { Component, OnInit, Input, HostListener, ViewChild, ElementRef, ViewChildren } from '@angular/core';
import { IUProperty } from 'src/app/shared/interface/umbraco-property';
import { IPictureData } from 'src/app/shared/components/picture/picture.component';
import { ThumbnailBuilderService } from './service/thumbnail-builder.service';
import { MqService } from 'src/app/service/mq.service';
import { config } from '../../app.config';
import get from 'lodash.get';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { timer } from 'rxjs';
import { IVideoPickerVideoData } from 'src/app/shared/interface/video-picker-data';
import { ISubtitleStyleSettings, ITitleStyleSettings } from '../../shared/interface/title-style-settings';
import { IItemSettings } from '../../shared/interface/item-settings';
import { applySubtitleSettings, applyTitleSettings } from '../../shared/utils/title-style-settings-utils';
import { applyItemSettings } from '../../shared/utils/item-settings-utils';

interface VideoTestimonialsPanelData {
  title?: IUProperty<string>;
  subtitle?: IUProperty<string>;
  capitalizeTitle?: IUProperty<string[]>;
  items: IUProperty<IVideoTestimonialsItemVm[]>;
  itemsOccupationSize?: IUProperty<string>;
  itemsOccupationFontWeight?: IUProperty<string>;
  itemsOccupationColor?: IUProperty<string>;

  titleStyleSettingsComposition?: ITitleStyleSettings;
  subtitleStyleSettingsComposition?: ISubtitleStyleSettings;
  itemSettingsComposition?: IItemSettings;
}

export interface IVideoTestimonialsItemVm {
  author: IUProperty<string>;
  image: IUProperty<IPictureData>;
  link: IUProperty<string>;
  linkColor: IUProperty<string>;
  occupation: IUProperty<string>;
  quote: IUProperty<string>;
  textColor: IUProperty<string>;
}

enum VideoState {
  active = 'active',
  inactive = 'inactive'
}

export interface IVideoViewModel {
  video: object;
  thumbnail: IPictureData;
  params: string;
}

@Component({
  selector: 'app-video-testimonials-panel',
  templateUrl: './video-testimonials-panel.component.html',
  styleUrls: ['./video-testimonials-panel.component.less'],
  animations: [
    trigger('video', [
      state('active', style({
        display: 'grid',
        opacity: '1'
      })),
      state('inactive', style({
        opacity: '0',
        display: 'none'
      })),
      transition('* => *', [animate('.5s ease-in-out')])
    ]),
    trigger('videoPicker', [
      state('active', style({
        backgroundColor: 'gray'
      })),
      state('inactive', style({
        backgroundColor: 'ghostwhite'
      })),
      transition('* => *', [animate('.3s ease-in-out')])
    ])
  ]
})

export class VideoTestimonialsPanelComponent implements OnInit {
  @Input() data: VideoTestimonialsPanelData;
  @ViewChild('panelEl', { static: false }) panelEl: ElementRef<HTMLElement>;
  @ViewChild('titleEl', { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild('subtitleEl', { static: false }) subtitleEl: ElementRef<HTMLElement>;
  @ViewChildren("carouselEl") carouselEl;

  @HostListener('window:scroll', []) onWindowScroll() {
    this.scrollPosition = window.pageYOffset;
    if ((this.scrollPosition + window.outerHeight) > this.panelEl.nativeElement.offsetTop) {
      this.loadMedia = true;
    }
  }

  videoData: IVideoViewModel;
  items: any[] = [];
  isMobile: boolean;
  readonly desktopMediaQuery = config.desktopMediaQuery || '(min-width: 768px)';

  videoState = VideoState;
  currentVideo: number;
  timeLeft: number = 10;
  countDown: any;
  scrollPosition: number = null;
  loadMedia: boolean = false;

  constructor(
    private thumbnailBuilder: ThumbnailBuilderService,
    private mq: MqService,
    private hostRef: ElementRef,
  ) { }

  ngOnInit() {
    this.mq.mobileDesktop(this.changeToMobile.bind(this), this.changeToDesktop.bind(this));
    this.items = this.data.items.value.map(item => ({
      ...item,
      videoData: this.isMobile ? this.mobile(item) : this.desktop(item)
      })
    );

    this.items = this.items.map(item => {
      item.state = VideoState.inactive;
      item.isShow = false;

      return item;
    });
    this.items[0].state = this.videoState.active;
    this.currentVideo = 0;

    this.startTimer();
  }

  ngAfterViewInit(): void {
    this.scrollPosition = window.pageYOffset;
    if ((this.scrollPosition + window.outerHeight) > this.panelEl.nativeElement.offsetTop) {
      this.loadMedia = true;
    }

    this.setCustomThemeProperties(this.data);
  }

  private setCustomThemeProperties(d: VideoTestimonialsPanelData) {
    applyTitleSettings(d, this.titleEl);
    applySubtitleSettings(d, this.subtitleEl);

    if (this.carouselEl && d.items.value.length > 0) { 
      this.carouselEl.forEach((ref, i) => {
        applyItemSettings(d, ref);

        if (d.itemsOccupationSize && d.itemsOccupationSize.value) {
          ref.nativeElement.style.setProperty("--video-testimonials-occupation-size",
            d.itemsOccupationSize.value + 'px');
        }
        if (d.itemsOccupationFontWeight && d.itemsOccupationFontWeight) {
          ref.nativeElement.style.setProperty("--video-testimonials-occupation-font-weight",
            d.itemsOccupationFontWeight.value);
        }
        if (d.itemsOccupationColor && d.itemsOccupationColor) {
          ref.nativeElement.style.setProperty("--video-testimonials-occupation-color",
            d.itemsOccupationColor.value);
        }
      });
    }


    if (d && d.capitalizeTitle && d.capitalizeTitle.value) {
      d.capitalizeTitle.value.forEach(item => {
        switch (item) {
          case 'Title': if (this.titleEl && this.titleEl.nativeElement) this.titleEl.nativeElement.style.textTransform = 'uppercase';
            break;
          case 'Subtitle': if (this.subtitleEl && this.subtitleEl.nativeElement) this.subtitleEl.nativeElement.style.textTransform = 'uppercase';
            break;
        }
      });
    }
  }

  changeToMobile() {
    this.isMobile = true;
  }

  changeToDesktop() {
    this.isMobile = false;
  }

  mobile(item) {
     return this.prepareVm(get(item, 'video.value.mobile'));
  }
  desktop(item) {
    return this.prepareVm(get(item, 'video.value.desktop'));
  }

  open() {
    this.items[this.currentVideo].isShow = true;
    this.pauseTimer();
  }
  close() {
    this.items[this.currentVideo].isShow = false;
  }

  private prepareVm(data: IVideoPickerVideoData): IVideoViewModel
  {
    if (!data) return null;

    return {
      thumbnail: this.thumbnailBuilder.create(data.thumbnail),
      params: `?controls=1&muted=0&autoplay=1&showinfo=0`,
      video: {
        url: data.url,
        type: data.type,
      }
    };
  }

  toggleVideoState(item: any, i: number) {

    if (item.state == VideoState.active)
    {
      return;
    } else {
      item.state = this.videoState.active;
      this.items[this.currentVideo].state = this.videoState.inactive;

      this.currentVideo = i;
    }
  }

  nextVideo() {
    if (this.currentVideo === this.itemLength() - 1) {
      this.items[0].state = this.videoState.active;
      this.items[this.currentVideo].state = this.videoState.inactive;

      this.currentVideo = 0;
    } else {
      this.items[this.currentVideo + 1].state = this.videoState.active;
      this.items[this.currentVideo].state = this.videoState.inactive;

      this.currentVideo = this.currentVideo + 1;
    }
  }

  prevVideo() {
    if (this.currentVideo === 0) {
      this.items[this.itemLength() - 1].state = this.videoState.active;
      this.items[this.currentVideo].state = this.videoState.inactive;

      this.currentVideo = this.itemLength() - 1;
    } else {
      this.items[this.currentVideo - 1].state = this.videoState.active;
      this.items[this.currentVideo].state = this.videoState.inactive;

      this.currentVideo = this.currentVideo - 1;
    }
  }

  nextClick() {
    this.nextVideo();
    this.pauseTimer();
  }

  prevClick() {
    this.prevVideo();
    this.pauseTimer();
  }

  dotClick(item: any, i: number) {
    this.toggleVideoState(item, i);
    this.pauseTimer();
  }

  startTimer() {
    if (this.itemLength() === 1) {
      return;
    }

    const source = timer(1000, 1000);
    this.countDown = source.subscribe(val => {
      this.timeLeft--;
      if (this.timeLeft === 0) {
        this.timeLeft = 10;
        this.nextVideo();
      }
    });
  }

  pauseTimer() {
    if (this.countDown) this.countDown.unsubscribe();
  }

  itemLength(): number {
    return this.items.length;
  }
}
